import { useEffect, useRef, useState } from "react";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import DataTable from "react-data-table-component";
import DropdownWithValueName from "../../../common/DropdownWithValueName";
import { Button, Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
import { getAllActiveSuburbsService, deleteSuburbService } from "../../../../Services/CRMAdminServices/CRMSuburb";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import { getAllAreas, getAllStates } from "../../../../Services/MMPClientServices/PropertySearchService";
import * as XLSX from 'xlsx';

const customStyles = {
    head: {
        style: {
            fontWeight: '800',
            color: '#0000ff',
            fontSize: '14px'
        },
    },
};

type DropdownOption = {
    value: number;
    label: string;
};

const CRMSuburb = () => {
    const [formErrors, setFormErrors] = useState<any>({});
    const [selectedStateId, setSelectedStateId] = useState<string>("");
    const [suburbs, setSuburbs] = useState<any[]>([]);
    const [statesData, setStatesData] = useState<DropdownOption[]>([]);
    const [areasData, setAreasData] = useState<DropdownOption[]>([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedSuburbId, setSelectedSuburbId] = useState<number | null>(null);
    const [searchState, setSearchState] = useState('');
    const [searchArea, setSearchArea] = useState('');
    const [filteredSuburbs, setFilteredSuburbs] = useState<any[]>([]);
    const [searchSuburb, setSearchSuburb] = useState('');
    const [tableScrollHeight, setTableScrollHeight] = useState("400px");
    const tableWrapperRef = useRef<HTMLDivElement | null>(null);

    const [formData, setFormData] = useState({
        suburbName: "",
        postcode: "",
        areaId: "",
        stateId: "",
        growthRating: "",
    });

    const navigate = useSafeNavigate();


    const {
        data: allStatesData,
        isLoading: allStatesLoading,
        isError: allStatesIsError,
    } = useGetRequestQuery(
        ["getAllStates"],
        getAllStates,
        {
            enabled: true,
        }
    );

    useEffect(() => {
        if (allStatesData?.data && !allStatesIsError && !allStatesLoading) {
            const transformedData = allStatesData.data.map((state: any) => ({
                value: state.id,
                label: state.name
            }));
            setStatesData(transformedData);
        }
    }, [allStatesData, allStatesIsError, allStatesLoading]);

    const {
        data: allAreasData,
        isLoading: allAreasLoading,
        isError: allAreasIsError,
    } = useGetRequestQuery(
        ["getAllAreas", searchState],
        () => getAllAreas({
            searchId: searchState || "0",
            searchType: "area"
        }),
        {
            enabled: true,
        }

    );

    useEffect(() => {
        if (allAreasData?.data && !allAreasIsError && !allAreasLoading) {
            console.log("area response:", allAreasData.data);
            const transformedData = allAreasData.data.map((area: any) => ({
                value: area.id,
                label: area.name,
                stateId: area.stateId ?? searchState,
            }));
            setAreasData(transformedData);
            console.log('areasData:', transformedData);
        }
    }, [allAreasData, allAreasIsError, allAreasLoading, searchState]);



    const { data: suburbData, isLoading, isError, refetch } = useGetRequestQuery<any>(
        ['getAllActiveSuburbsService'],
        getAllActiveSuburbsService,
        { enabled: true }
    );

    useEffect(() => {
        if (suburbData && !isError && !isLoading) {
            const sortedSuburbs = (suburbData?.data || []).sort((a: any, b: any) =>
                a.suburbName.localeCompare(b.suburbName)
            );
            setSuburbs(sortedSuburbs);
            setFilteredSuburbs(sortedSuburbs);
        }
    }, [suburbData, isError, isLoading]);

    useEffect(() => {
        const updateTableHeight = () => {
            if (!tableWrapperRef.current) return;
            const wrapperHeight = tableWrapperRef.current.clientHeight;
            setTableScrollHeight(`${Math.max(wrapperHeight - 8, 200)}px`);
        };

        updateTableHeight();
        window.addEventListener("resize", updateTableHeight);
        return () => window.removeEventListener("resize", updateTableHeight);
    }, [filteredSuburbs.length, isLoading]);

    // Handle delete mutation
    const { mutate: deleteSuburb, isPending: isDeleting } = useMutationRequestQuery(
        "deleteSuburb",
        deleteSuburbService,
        {
            onSuccess: () => {
                toast.success("Suburb deleted successfully.");
                setDeleteModal(false);
                refetch()
            },
            onError: () => {
                toast.error("Failed to delete suburb.");
            },
        }
    );

    // Handle delete confirmation
    const confirmDelete = (suburbId: number) => {
        setSelectedSuburbId(suburbId);
        setDeleteModal(true);
    };

    // Execute delete operation
    const handleDeleteClick = () => {
        if (selectedSuburbId) {
            deleteSuburb(selectedSuburbId);
        }
    };

    // Export Excel function
    const handleExportExcel = () => {
        const headers = ["Suburb ID", "State", "Area", "Suburb", "Postcode", "Growth%", "Vacancy Rate", "Nearest CBD", "KM to CBD", "Nearest Rail", "KM to Rail", "Population Trend", "Dwelling Demand", "Employment Hub", "School Catchments", "Major Road Access"];
        const excelData = filteredSuburbs.map(row => [
            row.suburbId || '',
            row.stateName || '',
            row.areaName || '',
            row.suburbName || '',
            row.postcode || '',
            row.growthRating || '',
            row.vacancy || '',
            row.nearestCBD || '',
            row.kmToCBD || '',
            row.nearestRail || '',
            row.kmToRail || '',
            row.populationTrend || '',
            row.dwellingDemand || '',
            row.employmentHubs || '',
            row.schoolCatchments || '',
            row.majorRoadAccess || ''
        ]);

        // Create worksheet from data
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...excelData]);

        // Create workbook and add the worksheet
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Suburbs');

        // Generate Excel file and trigger download
        XLSX.writeFile(workbook, `suburbs_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    //  const handleSearch = () => {
    //     let filtered = suburbs;
    //     if (searchState) {
    //         filtered = filtered.filter(suburb => 
    //             suburb.stateName.toLowerCase().includes(searchState.toLowerCase())
    //         );
    //     }
    //     if (searchArea) {
    //         filtered = filtered.filter(suburb => 
    //             suburb.areaName.toLowerCase().includes(searchArea.toLowerCase())
    //         );
    //     }
    //     setFilteredSuburbs(filtered);
    // };


    const handleSearch = () => {
        let filtered = suburbs;

        const selectedStateName = statesData.find(
            (s) => s.value === Number(searchState)
        )?.label;

        const selectedAreaName = areasData.find(
            (a) => a.value === Number(searchArea)
        )?.label;

        if (selectedStateName) {
            filtered = filtered.filter(
                (suburb) =>
                    suburb.stateName.toLowerCase() === selectedStateName.toLowerCase()
            );
        }

        if (selectedAreaName) {
            filtered = filtered.filter(
                (suburb) =>
                    suburb.areaName.toLowerCase() === selectedAreaName.toLowerCase()
            );
        }

        if (searchSuburb.trim() !== "") {
            filtered = filtered.filter(
                (suburb) =>
                    suburb.suburbName.toLowerCase().includes(searchSuburb.toLowerCase())
            );
        }

        console.log("Final filtered suburbs set to state:", filtered);
        setFilteredSuburbs(filtered);
    };


    // const handleSearch = () => {

    //     let filtered = suburbs;

    //     const selectedStateName = statesData.find(
    //         (s) => s.value === Number(searchState)
    //     )?.label;

    //     const selectedAreaName = areasData.find(
    //         (a) => a.value === Number(searchArea)
    //     )?.label;

    //     if (selectedStateName) {
    //         filtered = filtered.filter(
    //             (suburb) =>
    //                 suburb.stateName.toLowerCase() === selectedStateName.toLowerCase()
    //         );
    //     }

    //     if (selectedAreaName) {
    //         filtered = filtered.filter(
    //             (suburb) =>
    //                 suburb.areaName.toLowerCase() === selectedAreaName.toLowerCase()
    //         );
    //     }

    //     console.log("Final filtered suburbs set to state:", filtered);
    //     setFilteredSuburbs(filtered);
    // };



    // Table columns
    const columns = [
        {
            name: "Action",
            cell: (row: any) => (
                <ul className="action simple-list flex-row">
                    <li className="edit">
                        <Link to={`/EditSuburb/${row.suburbId}`}>
                            <i className="icon-pencil-alt" />
                        </Link>
                    </li>
                    <li className="delete">
                        <a onClick={() => confirmDelete(row.suburbId)} href="#">
                            <i className="icon-trash" />
                        </a>
                    </li>
                </ul>
            ),
            width: '5rem'
        },
        {
            name: "State",
            selector: (row: any) => row.stateName,
            sortable: false,
            width: "5rem"
        },
        {
            name: "Area",
            selector: (row: any) => row.areaName,
            sortable: false,
            width: "12rem"
        },
        {
            name: "Suburb",
            selector: (row: any) => row.suburbName,
            sortable: true,
            width: "12rem"
        },


        {
            name: "Postcode",
            selector: (row: any) => row.postcode,
            sortable: false,
            width: "6rem"
        }
        ,


        {
            name: "Growth%",
            selector: (row: any) => row.growthRating,
            sortable: false,
            width: "8rem"
        },
        {
            name: "Nearest CBD",
            selector: (row: any) => row.nearestCBD,
            sortable: false,
            width: "10rem"
        },
        {
            name: "KM to CBD",
            selector: (row: any) => row.kmToCBD,
            sortable: false,
            width: "8rem"
        },
        {
            name: "Nearest Rail",
            selector: (row: any) => row.nearestRail,
            sortable: false,
            width: "10rem"
        },
        {
            name: "KM to Rail",
            selector: (row: any) => row.kmToRail,
            sortable: false,
            width: "8rem"
        },
        {
            name: "Population Trend",
            selector: (row: any) => row.populationTrend,
            sortable: false,
            width: "10rem"
        },
        {
            name: "Dwelling Demand",
            selector: (row: any) => row.dwellingDemand,
            sortable: false,
            width: "10rem"
        },
        {
            name: "Employment Hub",
            selector: (row: any) => row.employmentHubs,
            sortable: false,
            width: "10rem"
        },
        {
            name: "School Catchments",
            selector: (row: any) => row.schoolCatchments,
            sortable: false,
            width: "10rem"
        },
        {
            name: "Major Road Access",
            selector: (row: any) => row.majorRoadAccess,
            sortable: false,
            width: "10rem"
        }

        // {
        //     name: "Growth Rating",
        //     selector: (row: any) => row.growthRating,
        //     sortable: false,
        //     //   width: "18rem",
        //     cell: (row: any) => {
        //         const value = parseInt(row.growthRating, 10);

        //         if (value >= 1 && value <= 5) {
        //             return (
        //                 <div>
        //                     {Array.from({ length: 5 }).map((_, i) => (
        //                         <span
        //                             key={i}
        //                             style={{
        //                                 color: i < value ? "gold" : "lightgray",
        //                                 fontSize: "25px",   // increase size here
        //                                 marginRight: "2px", // small spacing
        //                             }}
        //                         >
        //                             ★
        //                         </span>
        //                     ))}
        //                 </div>
        //             );
        //         }

        //         return <></>; // empty if value not 1–5
        //     }
        // }

    ];





    return (
        <>
            <div className="px-4 pt-4" style={{
                height: '90vh',
                overflow: 'hidden',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                backgroundColor: '#f9f9f9',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <div style={{
                    borderRadius: '8px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    padding: '10px',
                    backgroundColor: '#fff',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <div className="g-3 mb-2 d-flex justify-content-between align-items-center">
                        <h3 className="f-w-900 heading-color">Suburb List</h3>
                        <div className="d-flex gap-2 align-items-center">
                            <Button color="primary" onClick={() => navigate("/CRMarea")}>Add Area</Button>
                            <Button color="primary" onClick={() => navigate("/AddSuburb")}>Add Suburb</Button>
                            <Button color="success" onClick={handleExportExcel}>Export Excel</Button>
                        </div>
                    </div>

                    <div className="d-flex align-items-center mb-3">
                        <div className="me-3">
                            <label className="me-2">State:</label>
                            <select
                                value={searchState}
                                onChange={e => {
                                    setSearchState(e.target.value);
                                    setSearchArea('');
                                }}
                                className="form-control d-inline-block"
                                style={{ width: 180 }}
                            >
                                <option value="">All States</option>
                                {statesData.map((state: any) => (
                                    <option key={state.value} value={state.value}>
                                        {state.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="me-3">
                            <label className="me-2">Area:</label>
                            <select
                                value={searchArea}
                                onChange={e => setSearchArea(e.target.value)}
                                className="form-control d-inline-block"
                                style={{ width: 180 }}
                            >
                                <option value="">All Areas</option>
                                {(areasData || [])
                                    .filter((area: any) =>
                                        !searchState || Number(area.stateId) === Number(searchState)
                                    )
                                    .sort((a: any, b: any) => a.label.localeCompare(b.label))
                                    .map((area: any) => (
                                        <option key={area.value} value={area.value}>
                                            {area.label}
                                        </option>
                                    ))}
                            </select>
                        </div>

                        <div className="me-3">
                            <label className="me-2">Suburb Name:</label>
                            <input
                                type="text"
                                value={searchSuburb}
                                onChange={e => setSearchSuburb(e.target.value)}
                                className="form-control d-inline-block"
                                style={{ width: 200 }}
                                placeholder="Enter suburb name"
                            />
                        </div>

                        <Button color="success" onClick={handleSearch}>Search Now</Button>
                    </div>

                    {isLoading ? (
                        <div
                            style={{
                                borderRadius: '8px',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                padding: '12px',
                                backgroundColor: '#fff',
                                textAlign: 'center',
                            }}
                            className='mt-4 mb-2'
                        >
                            <Spinner color="primary" />
                        </div>
                    ) : filteredSuburbs.length !== 0 ? (
                        <div ref={tableWrapperRef} style={{ flex: 1, minHeight: 0 }}>
                            <DataTable
                                columns={columns}
                                data={filteredSuburbs}
                                customStyles={customStyles}
                                responsive
                                fixedHeader
                                fixedHeaderScrollHeight={tableScrollHeight}
                                persistTableHead
                            />
                        </div>
                    ) : (
                        <div
                            style={{
                                borderRadius: '8px',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                padding: '12px',
                                backgroundColor: '#fff',
                                textAlign: 'center',
                            }}
                            className='mt-4 mb-2'
                        >
                            <h6>No suburb data available.</h6>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)} centered>
                <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>Confirm Delete</ModalHeader>
                <ModalBody>Are you sure you want to delete this suburb?</ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setDeleteModal(false)}>Cancel</Button>
                    <Button color="danger" onClick={handleDeleteClick} disabled={isDeleting}>
                        {isDeleting ? "Deleting..." : "Delete"}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default CRMSuburb;